import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { useCases } from "@/lib/cases";
import { Newspaper, ShieldCheck } from "lucide-react";
import { useT } from "@/lib/i18n";

export const Route = createFileRoute("/services/casos")({
  component: Casos,
  head: () => ({
    meta: [
      { title: "Case studies — PULSO33" },
      { name: "description", content: "Selected PULSO33 projects: AI agents in production, edge migrations, BI platforms and end-to-end automation." },
      { property: "og:title", content: "Case studies — PULSO33" },
      { property: "og:description", content: "Real systems we've shipped." },
      { property: "og:url", content: "https://pulso33.com/services/casos" },
    ],
    links: [{ rel: "canonical", href: "https://pulso33.com/services/casos" }],
  }),
});

function Casos() {
  const { tk } = useT();
  const cases = useCases();

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-gold/15 bg-card/30">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("cases.kicker", "Case studies")}
          </span>
          <h1 className="font-serif text-4xl md:text-6xl mt-5 max-w-4xl mx-auto">
            {tk("cases.title.a", "Real systems, ")}
            <em className="not-italic gold-gradient">{tk("cases.title.b", "real outcomes")}</em>
          </h1>
          <p className="mt-6 text-foreground/80 max-w-2xl mx-auto leading-relaxed">
            {tk(
              "cases.intro",
              "A selection of recent PULSO33 projects. We respect every client's NDA — names removed, outcomes intact.",
            )}
          </p>
          <div className="mt-10 inline-flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-foreground/70 border border-gold/40 bg-background/40 backdrop-blur-sm rounded-full px-5 py-2.5">
            <ShieldCheck size={14} className="text-gold" />
            {tk("cases.disclaimer", "Client identities anonymised by default.")}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 py-20 space-y-16">
          {cases.map((c, idx) => (
            <article
              key={c.slug}
              id={c.slug}
              className="scroll-mt-32 rounded-2xl border border-gold/20 bg-card/30 p-8 md:p-12"
            >
              <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-gold/80">
                <Newspaper size={14} />
                <span>{c.area}</span>
                <span className="text-muted-foreground/60">· {c.year}</span>
                <span className="ml-auto text-muted-foreground/60">
                  #{String(idx + 1).padStart(2, "0")}
                </span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl mt-5 leading-snug">{c.headline}</h2>
              <div className="gold-divider my-6 max-w-[80px]" />
              <p className="text-foreground/80 leading-relaxed">{c.summary}</p>
              <div className="mt-6 text-xs tracking-[0.25em] uppercase text-gold/80">{c.role}</div>
            </article>
          ))}

          <p className="text-center text-sm text-muted-foreground italic max-w-2xl mx-auto">
            {tk(
              "cases.more",
              "And many more we can't share publicly. Ask us — under NDA we can walk you through the relevant ones for your industry.",
            )}
          </p>
        </div>
      </section>
    </Layout>
  );
}
